import { ReactElement, useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useSearchParams } from 'react-router-dom';

import { useAuthStore, usePetitionStore } from '../../store';
import Table from '../../components/table/table.component';
import { Petition, PetitionsHistoryResponse } from '../../types/petition.type';
import { PetitionStatusColors } from '../../utils/petition-status.util';
import { useNavigation } from '../../hooks/navigation.hook';
import DatePicker from '../../components/date-picker/date-picker.component';
import StatusFilterButton from '../../components/status-filter-button/status-filter-button';
import View from '../../components/view/view';
import { GetAllFilters } from '../../services/petition.service';
import { RolePermission } from '../../types/operator.type';
import { PermissionCallbackBoolean } from '../../types/permission-callback';
import DownloadFileButton from '../../components/download-file-button/download-file-button';
import { DateRange } from '../../types/date-range.type';
import { biSDK } from '../../services/bi-api';
import { generateDownloadableFile } from '../../utils/file.util';
import { Toast } from '../../components/toast/toast';
import { mapSearchParams } from '../../utils/search-params';
import { hasPermissions } from '../../utils/permissions';
import { useSettingsStore } from '../../store/settings-store';

const PetitionsHistoryView = () => {
    const { navigate } = useNavigation();
    const [params, setParams] = useSearchParams({ page: '1', count: '10' });

    const { theme } = useSettingsStore();
    const petitionState = usePetitionStore();
    const { permissions, accessToken } = useAuthStore();

    const [rows, setRows] = useState<any[]>([]);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        listPetitions(permissions, mapSearchParams(params) as any);
    }, [params, permissions]);

    useEffect(() => {
        mapPetitions(petitionState.historyList);
    }, [petitionState.historyList]);

    const canRead: PermissionCallbackBoolean = useCallback(
        (permissions: RolePermission[]) => {
            return Boolean(
                permissions.find((per) => per.name === 'read-petitions')
            );
        },
        []
    );

    const listPetitions = useCallback(
        (permissions: RolePermission[], params?: GetAllFilters) => {
            if (!canRead(permissions)) return;
            petitionState.fetchAll(params);
        },
        []
    );

    const mapStatus = (petition: Petition) => {
        if (!petition.isFinished && !petition.cancellationReasonId)
            return PetitionStatusColors.ACTIVE;
        if (petition.isFinished) return PetitionStatusColors.COMPLETED;

        return PetitionStatusColors.CANCELLED;
    };

    const mapDirection = (direction: string) => {
        const text = direction.substring(0, 25);
        return `${text}${direction.length > 25 ? '...' : ''}`;
    };

    const mapPetitions: (response: PetitionsHistoryResponse) => void =
        useCallback((response) => {
            const { total, result } = response;
            setTotalPages(Math.ceil(total / Number(params.get('count'))));
            setRows(
                result.map((petition) => ({
                    id: petition.id,
                    data: [
                        { type: 'number', value: petition.id },
                        {
                            type: 'string',
                            value: mapDirection(petition.googleReferencePoint),
                        },
                        {
                            type: 'string',
                            value: `${petition.user?.firstName ?? '-'} ${
                                petition.user?.lastName ?? '-'
                            }`,
                        },
                        { type: 'status', value: mapStatus(petition) },
                        { type: 'date', value: new Date(petition.createdAt) },
                    ],
                }))
            );
        }, []);

    const onPageChange = (page: number) => {
        if (page < 1 || page > totalPages) return;
        params.set('page', page.toString());
        setParams(params);
    };

    const onPetitionSelected = (id: number) => {
        navigate(`/petitions/${id}`);
    };

    const addFilter = useCallback(
        (field: string, value: string) => {
            if (!value || value.toString().trim().length === 0) {
                params.delete(field);
            } else {
                params.set(field, value);
            }
            params.set('page', '1');
            setParams(params);
        },
        [params]
    );

    const clearFilter = useCallback(
        (field: string) => {
            params.delete(field);
            params.set('page', '1');
            setParams(params);
        },
        [params]
    );

    const downloadExcel: (range: DateRange) => Promise<void> = useCallback(
        async (range) => {
            if (!accessToken) return;
            Toast.show({ text: 'Descargando...', type: 'info' });
            const file = await biSDK.petitions.downloadExcel(
                range,
                accessToken
            );
            if (!file) {
                Toast.show({
                    text: 'Error descargando el archivo',
                    type: 'error',
                });
            }
            generateDownloadableFile(file, 'petitions.xlsx');
        },
        [accessToken]
    );

    const renderDownloadPetitionsButton: () => ReactElement =
        useCallback(() => {
            if (!hasPermissions('download-petitions', permissions))
                return <></>;
            return (
                <DownloadFileButton
                    isDateLimited={true}
                    onClick={downloadExcel}
                >
                    <span>Descargar solicitudes</span>
                </DownloadFileButton>
            );
        }, [permissions, downloadExcel]);

    return (
        <View
            theme={theme}
            permissions={permissions}
            requiredPermission="read-petitions"
        >
            <div className={'viewContent'}>
                <h1 className={'view-title'}>Solicitudes de emergencia</h1>
                <div className={'flex align-center'} style={{ gap: 8 }}>
                    <DatePicker
                        onDate={(date) =>
                            addFilter('createdAt', format(date, 'yyyy-MM-dd'))
                        }
                        clearable={true}
                        onClear={() => clearFilter('createdAt')}
                    />
                    <StatusFilterButton
                        defaultValue={
                            params.get('status')
                                ? Number(params.get('status'))
                                : undefined
                        }
                        statusList={[
                            { value: -1, text: 'Todas' },
                            { value: 1, text: 'Activas' },
                            { value: 2, text: 'Culminadas' },
                            { value: 3, text: 'Canceladas' },
                        ]}
                        onSelect={(status) =>
                            status.value === -1
                                ? clearFilter('status')
                                : addFilter('status', status.value.toString())
                        }
                    />
                    {renderDownloadPetitionsButton()}
                </div>
                <Table
                    header={[
                        'ID',
                        'Dirección',
                        'Usuario',
                        'Estado',
                        'Fecha de solicitud',
                    ]}
                    data={rows}
                    currentPage={Number(params.get('page'))}
                    totalPages={totalPages}
                    onPageChange={onPageChange}
                    onItemSelected={onPetitionSelected}
                />
            </div>
        </View>
    );
};

export default PetitionsHistoryView;
